import React, { useLayoutEffect, useState } from "react";
import { Upload } from "antd";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import {
   getBoard,
   sendFile,
   updateBoardMemberRole
} from "../../../../../../services/restAPI/RestApi";
import IntlMessages from "@util/IntlMessages";
import "./EditModal.less";
import { updateBoards, getMemberId, updateTranslationsUsage } from "../../../../../../util/Scripts";
import { updateBoardOrder, editBoard } from "../../../../../../services/restAPI/RestApi";
import { useSelector } from "react-redux";
import { Message, Switch, Input, Modal } from "../../../../../BP2Components";
import { Icon_Plus } from "../../../../../BP2Components/Icons/icons";

const EditModal = ({ boardInfo, visible, handleExit }) => {
   const intl = useIntl();
   const [fileList, setFileList] = useState([]);
   const [iconLoading, setIconLoading] = useState(false);
   const [currentBoard, setCurrentBoard] = useState(boardInfo);

   const { allLanguages } = useSelector(({ settings }) => settings);

   const handleIconUpload = async (info) => {
      if (info.file.status === "uploading") {
         setIconLoading(true);
         return;
      }
      if (info.file.status === "done") {
         try {
            setCurrentBoard((prevBoard) => ({ ...prevBoard, boardIconId: info.file.response }));
            setFileList(info.fileList);
            setIconLoading(false);
         } catch (error) {
            Message.error(intl.formatMessage({ id: "file.uploadFailed" }));
         }
         return;
      }

      if (info.file.status === "error") {
         setIconLoading(false);
      }
   };

   const handleCancel = () => {
      if (JSON.stringify(currentBoard) !== JSON.stringify(boardInfo)) {
         confirm(intl.formatMessage({ id: "boardsManager.exitWithoutSaveConfirm" })) == true &&
            handleExit();
         return;
      }

      handleExit();
   };

   const handleSave = (board) => async () => {
      if (board?.sheets) {
         try {
            Message.loading(intl.formatMessage({ id: "global.loading" }), 0);

            if (board.showInMainNav !== boardInfo.showInMainNav) {
               const detailedBoardInfo = (await getBoard(board.id)).data;

               await updateBoardMemberRole(
                  getMemberId(detailedBoardInfo),
                  board.id,
                  board.memberType,
                  board.showInMainNav
               );
            }

            if (board.boardOrder !== boardInfo.boardOrder) {
               await updateBoardOrder(board.id, board.boardOrder);
            }

            await editBoard(board);

            //TODO: Remove this after backend fixes translations
            updateTranslationsUsage(intl, true);
            updateBoards(intl);
         } catch (err) {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         }
      }
   };

   const handleRemoveIcon = () => {
      // This timeout needs to be here to wait for animation to finish
      setTimeout(() => setFileList([]), [300]);
      setCurrentBoard((prevBoard) => ({ ...prevBoard, boardIconId: "" }));
   };

   const customUpload = async (uploadObject) => {
      const { file, onError, onSuccess } = uploadObject;
      try {
         const fmData = new FormData();
         fmData.append("multipartFile", file);

         const res = await sendFile(fmData);
         onSuccess(res.data);
      } catch (err) {
         onError({ err });
      }
   };

   const handleSwitch = (optionName) => (value) => {
      setCurrentBoard((prevBoard) => ({ ...prevBoard, [optionName]: value }));
   };

   const handleOrderChange = ({ target }) => {
      setCurrentBoard((prevBoard) => ({
         ...prevBoard,
         boardOrder: target?.value !== "0" && !target?.value ? null : Number(target.value)
      }));
   };

   const handleKeyChange = ({ target }) => {
      setCurrentBoard((prevBoard) => ({
         ...prevBoard,
         name: target.value
      }));
   };

   const handleNameChange =
      (language) =>
      ({ target }) =>
         setCurrentBoard((prevBoard) => {
            const translations = prevBoard.translations ?? [];
            const boardTranslationsIndex = translations.findIndex(
               (translation) => translation.key === prevBoard.name
            );

            // If translation for this board is present we modify the object
            if (boardTranslationsIndex !== -1) {
               translations[boardTranslationsIndex].values[language] = target.value;
            } else {
               // If it's missing, we add new one
               translations.push({ key: prevBoard.name, values: { [language]: target.value } });
            }

            return { ...prevBoard, translations };
         });

   useLayoutEffect(() => {
      if (currentBoard.iconLink) {
         setFileList([
            {
               uid: currentBoard.id,
               name: "Image",
               status: "done",
               url: currentBoard.iconLink,
               thumbUrl: currentBoard.iconLink
            }
         ]);
      }
   }, []);

   return (
      <Modal
         isOpen={visible}
         onCancel={handleCancel}
         onOk={handleSave(currentBoard)}
         topBarContent={intl.formatMessage({ id: "boardsManager.editBoard" })}
         className="edit-board-modal"
      >
         <div className="edit-modal">
            <div className="left-options">
               <div className="option">
                  <IntlMessages id="boardsManager.boardIcon" />
                  <Upload
                     listType="picture-card"
                     customRequest={customUpload}
                     onChange={handleIconUpload}
                     defaultFileList={fileList}
                     onRemove={handleRemoveIcon}
                  >
                     {!fileList?.length && !iconLoading && <Icon_Plus/>}
                  </Upload>
               </div>
               <div className="option">
                  <IntlMessages id="boardsManager.showInMainNav" />
                  <Switch
                     defaultChecked={currentBoard.showInMainNav}
                     className="switch"
                     onChange={handleSwitch("showInMainNav")}
                  />
               </div>
            </div>
            <div className="right-options">
               <div className="option">
                  <IntlMessages id="boardsManager.boardName" />
                  <Input
                     addonBefore={intl.formatMessage({ id: "global.key" })}
                     onChange={handleKeyChange}
                     defaultValue={currentBoard.name}
                  />
                  {allLanguages?.map(({ language }) => (
                     <Input
                        key={language}
                        id={`${language}-input`}
                        addonBefore={language.toUpperCase()}
                        onChange={handleNameChange(language)}
                        defaultValue={
                           currentBoard.translations?.find(
                              (translation) => translation.key === currentBoard.name
                           )?.values?.[language]
                        }
                     />
                  ))}
               </div>
               <div className="option">
                  <IntlMessages id="boardsManager.boardOrder" />
                  <Input
                     type="number"
                     min={0}
                     defaultValue={currentBoard.boardOrder}
                     onChange={handleOrderChange}
                     allowClear
                  />
               </div>
            </div>
         </div>
      </Modal>
   );
};

EditModal.propTypes = {
   boardInfo: PropTypes.object.isRequired,
   visible: PropTypes.bool.isRequired,
   handleExit: PropTypes.func.isRequired
};

export default EditModal;
